import type { ToggleUserActiveReason } from '@core/livestream/constants'
import type { Invitation } from '@core/livestream/types'
import { logInfo } from '@core/logging'
import type { NamedSet } from '@core/zustand'

import type { ILiveStreamState } from './state'

const HEARTBEAT_INTERVAL = 30 * 1000 // 30sec

export type ILiveStreamAction = ReturnType<typeof createLiveStreamActions>

/**
 * Actions for live stream
 */
export function createLiveStreamActions(set: NamedSet<ILiveStreamState>) {
  return {
    heartbeatIntervalSet: (callback: () => void) => {
      set(
        (previouseState) => {
          if (!previouseState.heartbeatInterval) {
            const interval = setInterval(callback, HEARTBEAT_INTERVAL)
            return {
              heartbeatInterval: interval,
            }
          }
          return previouseState
        },
        false,
        'heartbeatIntervalSet',
      )
    },
    heartbeatIntervalCleared: () =>
      set(
        (previousState) => {
          if (previousState.heartbeatInterval) {
            clearInterval(previousState.heartbeatInterval)
            return { heartbeatInterval: undefined }
          }
          return previousState
        },
        false,
        'heartbeatIntervalCleared',
      ),
    joinedTopicsAdded: (topic: string, onSuccess?: () => void) =>
      set(
        (previousState) => {
          if (!previousState.joinedTopics.includes(topic)) {
            onSuccess?.()

            return {
              joinedTopics: [...previousState.joinedTopics, topic],
            }
          }
          return previousState
        },
        false,
        'joinedTopicsAdded',
      ),
    joinedTopicsRemoved: (topic: string | undefined, onSuccess?: () => void) =>
      set(
        (previousState) => {
          if (!topic) {
            return previousState
          }
          onSuccess?.()
          return {
            joinedTopics: previousState.joinedTopics.filter(
              (current) => current !== topic,
            ),
          }
        },
        false,
        'joinedTopicsRemoved',
      ),

    /** Host and Co-host web streaming invitation */
    inviteReceived: (invitation: Invitation, liveStreamId: string) => {
      set(
        (previousState) => ({
          invitations: {
            ...previousState.invitations,
            [liveStreamId]: invitation,
          },
        }),
        false,
        'inviteReceived',
      )
    },
    inviteDismissed: (invitationToken: string, liveStreamId: string) => {
      set(
        (previousState) => ({
          invitationsTokensDismissed: {
            ...previousState.invitationsTokensDismissed,
            [liveStreamId]: {
              ...previousState.invitationsTokensDismissed[invitationToken],
              [invitationToken]: true,
            },
          },
        }),
        false,
        'inviteDismissed',
      )
    },

    usernameHydratedSet: (username: string) => {
      set(() => {
        return {
          usernameHydrated: {
            name: username,
            isUserDefined: true,
          },
        }
      })
    },

    usernameSet: (username: string, isUserDefined?: boolean) => {
      set(
        (previousState) => {
          // isUserDefined is whether the username is the name set by the user as
          // opposed to auto-generated name by the system. If null is passed, it
          // will take the value previously set. If there is no value previously
          // set, it will be false.
          const newisUserDefined =
            isUserDefined ?? previousState.username?.isUserDefined ?? false

          return {
            username: {
              name: username,
              isUserDefined: newisUserDefined,
            },
          }
        },
        false,
        'usernameSet',
      )
    },

    setActiveWatch: (
      liveStreamId: string,
      reason: ToggleUserActiveReason,
    ): void => {
      logInfo('set active watch', reason)
      return set(
        (state) => ({
          activeWatches: {
            ...state.activeWatches,
            [liveStreamId]: true,
          },
        }),
        false,
        `setActiveWatch - ${reason}`,
      )
    },
    unsetActiveWatch: (
      liveStreamId: string,
      reason: ToggleUserActiveReason,
    ): void => {
      logInfo('unset active watch', reason)
      return set(
        (state) => ({
          activeWatches: {
            ...state.activeWatches,
            [liveStreamId]: false,
          },
        }),
        false,
        `unsetActiveWatch - ${reason}`,
      )
    },
    clearActiveWatches: (reason: ToggleUserActiveReason): void => {
      logInfo('clear active watches', reason)
      return set({ activeWatches: {} }, false, `clearActiveWatches - ${reason}`)
    },
    setHeartsCount: (liveStreamId: string, count: number) => {
      set(
        (state) => ({
          heartsCount: {
            ...state.heartsCount,
            [liveStreamId]: Math.max(
              state.heartsCount[liveStreamId] ?? 0,
              count,
            ),
          },
        }),
        false,
        'setHeartsCount',
      )
    },
    setEnteredLiveStreamGiveaway: (
      liveStreamId: string | undefined,
      interactionId: string,
      email: string,
    ) => {
      set((state) => {
        if (!liveStreamId) {
          return state
        }
        return {
          liveStreamGiveaways: {
            ...state.liveStreamGiveaways,
            [liveStreamId]: {
              ...state.liveStreamGiveaways[liveStreamId],
              [interactionId]: { emailEntered: email },
            },
          },
        }
      })
    },
    setWinnerRevealedLiveStreamGiveaway: (
      liveStreamId: string | undefined,
      interactionId: string,
    ) => {
      set((state) => {
        if (!liveStreamId) {
          return state
        }
        const giveaways = state.liveStreamGiveaways[liveStreamId]
        const current = giveaways?.[interactionId]
        return {
          liveStreamGiveaways: {
            ...state.liveStreamGiveaways,
            [liveStreamId]: {
              ...giveaways,
              [interactionId]: {
                ...current,
                winnerRevealed: true,
              },
            },
          },
        }
      })
    },
    setDismissedLiveStreamGiveaway: (
      liveStreamId: string | undefined,
      interactionId: string,
    ) => {
      set((state) => {
        if (!liveStreamId) {
          return state
        }
        const giveaways = state.liveStreamGiveaways[liveStreamId]
        const current = giveaways?.[interactionId]
        return {
          liveStreamGiveaways: {
            ...state.liveStreamGiveaways,
            [liveStreamId]: {
              ...giveaways,
              [interactionId]: {
                ...current,
                winnerRevealed: !!current?.winnerRevealed,
                dismissed: true,
              },
            },
          },
        }
      })
    },
  }
}
